import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  alpha,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Popover,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { url } from "../constants/url";
import AddTeamMembers from "./AddTeamMembers";
import ViewTeamMembers from "./ViewTeamMembers";
import {
  Add,
  Business,
  Groups,
  Visibility,
  Edit,
  CheckCircle,
  Cancel,
  Close,
  FilterList,
  Person,
  Email,
} from "@mui/icons-material";
import { useThemeToggle } from "../context/ThemeContext";

const Companies = ({ userData }) => {
  const theme = useTheme();
  const { mode } = useThemeToggle();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const UID = userData?._id;
  const role = userData?.role;

  const [name, setName] = useState("");
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // dialogs
  const [openAddMembersPop, setOpenAddMembersPop] = useState(false);
  const [openViewMembers, setOpenViewMembers] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openAddCompanyDialog, setOpenAddCompanyDialog] = useState(false);

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [editData, setEditData] = useState({ name: "" });

  // Filter states
  const [statusFilter, setStatusFilter] = useState("all");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  // Theme colors from context
  const primaryColor = theme.palette.primary.main;
  const accentColor = theme.palette.secondary.main;

  // 🔹 Fetch companies
  const fetchCompanyData = async () => {
    try {
      let response;
      if (role === "superAdmin") {
        response = await fetch(`${url}/api/company/fetch-companies`);
      } else {
        response = await fetch(
          `${url}/api/company/fetch-companies?userId=${UID}`
        );
      }
      const data = await response.json();
      setCompanies(data?.data || []);
    } catch (error) {
      console.error("❌ Error fetching companies:", error);
    }
  };

  // 🔹 Add company
  const handleAddCompany = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      const response = await fetch(`${url}/api/admin/add-company`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: UID, name }),
      });
      const data = await response.json();
      if (response.ok) {
        setMessage("✅ Company added successfully!");
        setName("");
        setOpenAddCompanyDialog(false);
        fetchCompanyData();

        // 🎯 NOTIFICATION: Notify Super Admin about new company
        try {
          const superAdminResponse = await fetch(`${url}/api/users/super-admin`);
          const superAdminData = await superAdminResponse.json();
          const superAdminId = superAdminData._id;

          await fetch(`${url}/api/notifications/create`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              userId: superAdminId,
              type: "company_approval",
              title: "🏢 New Company Approval Request",
              message: `Company "${name}" created by ${userData?.email || "Admin"} needs approval.`,
              companyId: data._id,
              metadata: {
                companyName: name,
                adminEmail: userData?.email,
                adminName: userData?.name,
                submittedDate: new Date().toISOString(),
                actionRequired: "review_and_approve"
              }
            })
          });
        } catch (notifError) {
          console.warn("Notification failed:", notifError);
        }

      } else {
        setMessage("❌ " + (data.message || "Something went wrong"));
      }
    } catch (error) {
      setMessage("❌ Error: " + error.message);
    }
    setLoading(false);
  };

  const handleApproval = async (companyId, status) => {
    try {
      const response = await fetch(`${url}/api/super-admin/approve-company`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ companyId, status, superAdminId: UID }),
      });
      const data = await response.json();
      if (response.ok) {
        fetchCompanyData();

        // 🎯 NOTIFICATION: Notify Company Admin about approval status
        const company = companies.find(c => c._id === companyId);
        if (company) {
          try {
            await fetch(`${url}/api/notifications/create`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                userId: company.createdBy,
                type: "company_approval",
                title: status === "approved" ? "🎉 Company Approved!" : "❌ Company Rejected",
                message: status === "approved" 
                  ? `Great news! Your company "${company.name}" has been approved. You can now add team members and start managing leads.`
                  : `Your company "${company.name}" registration requires modifications. Please contact support for details.`,
                companyId: companyId,
                metadata: {
                  companyName: company.name,
                  status: status,
                  approvedBy: userData?.name || userData?.email,
                  decisionDate: new Date().toISOString(),
                  nextSteps: status === "approved" ? "add_team_members" : "contact_support"
                }
              })
            });
          } catch (notifError) {
            console.warn("Approval notification failed:", notifError);
          }
        }

      } else {
        console.error("❌ " + data.message);
      }
    } catch (error) {
      console.error("❌ Error approving company:", error);
    }
  };

  // 🔹 Edit company handlers
  const handleEditOpen = (company) => {
    setSelectedCompany(company._id);
    setEditData({ name: company.name });
    setOpenEditDialog(true);
  };

  const handleEditClose = () => setOpenEditDialog(false);

  const handleEditChange = (e) => {
    setEditData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleUpdateCompany = async () => {
    try {
      const response = await fetch(
        `${url}/api/company/update-company/${selectedCompany}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(editData),
        }
      );
      const data = await response.json();
      if (response.ok) {
        setMessage("✅ Company updated successfully!");
        setOpenEditDialog(false);
        fetchCompanyData();
      } else {
        alert("❌ " + (data.message || "Failed to update"));
      }
    } catch (error) {
      console.error("❌ Error updating company:", error);
    }
  };

  // member dialogs
  const handleAddMembersOpen = (companyId) => {
    setSelectedCompany(companyId);
    setOpenAddMembersPop(true);
  };
  const handleAddMembersClose = () => setOpenAddMembersPop(false);

  const handleViewMembersOpen = (companyId) => {
    setSelectedCompany(companyId);
    setOpenViewMembers(true);
  };
  const handleViewMembersClose = () => setOpenViewMembers(false);

  // Filter handlers
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleResetFilters = () => {
    setStatusFilter("all");
    handleFilterClose();
  };

  const applyFilters = () => {
    handleFilterClose();
  };

  const filterOpen = Boolean(filterAnchorEl);
  const isFilterActive = statusFilter !== "all";

  useEffect(() => {
    fetchCompanyData();
  }, [openAddMembersPop]);

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return { bg: alpha(theme.palette.success.main, 0.08), color: theme.palette.success.main };
      case "rejected":
        return { bg: alpha(theme.palette.error.main, 0.08), color: theme.palette.error.main };
      case "pending":
        return { bg: alpha(theme.palette.warning.main, 0.08), color: theme.palette.warning.main };
      default:
        return { bg: alpha(theme.palette.grey[500], 0.08), color: theme.palette.grey[500] };
    }
  };

  // Filter companies based on status
  const filteredCompanies = companies.filter(company => {
    if (statusFilter === "all") return true;
    return company.status === statusFilter;
  });

  // Get admin's company (single company for admin role)
  const adminCompany = companies.length > 0 ? companies[0] : null;
  const adminHasCompany = !!adminCompany;

  // 🔹 DataGrid for superAdmin
  const rows = filteredCompanies.map((c) => ({
    id: c._id,
    name: c.name,
    status: c.status,
    teamMembers: c.teamMembers.length,
    companyData: c,
  }));

  const columns = [
    { 
      field: "name", 
      headerName: "Company Name", 
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Business sx={{ color: accentColor, fontSize: 18 }} />
          <Typography variant="body2" fontWeight="500">
            {params.value}
          </Typography>
        </Box>
      )
    },
    { 
      field: "status", 
      headerName: "Status", 
      flex: 0.7,
      minWidth: 90,
      renderCell: (params) => (
        <Chip
          label={params.value.charAt(0).toUpperCase() + params.value.slice(1)}
          size="small"
          sx={{
            backgroundColor: getStatusColor(params.value).bg,
            color: getStatusColor(params.value).color,
            fontWeight: '500',
            fontSize: '0.7rem',
            height: '24px'
          }}
        />
      )
    },
    { 
      field: "teamMembers", 
      headerName: "Members", 
      flex: 0.5,
      minWidth: 80,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Groups sx={{ color: accentColor, fontSize: 16 }} />
          <Typography variant="body2" fontWeight="500">
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1.2,
      minWidth: 180,
      renderCell: (params) => {
        const company = params.row.companyData;
        return (
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            {role === "superAdmin" && (
              <>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<CheckCircle fontSize="small" />}
                  onClick={() => handleApproval(company._id, "approved")}
                  sx={{
                    backgroundColor: theme.palette.success.main,
                    '&:hover': { backgroundColor: alpha(theme.palette.success.main, 0.8) },
                    fontSize: '0.7rem',
                    px: 1,
                    minWidth: 'auto',
                    height: '28px'
                  }}
                >
                  Approve
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<Cancel fontSize="small" />}
                  onClick={() => handleApproval(company._id, "rejected")}
                  sx={{
                    backgroundColor: theme.palette.error.main,
                    '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.8) },
                    fontSize: '0.7rem',
                    px: 1,
                    minWidth: 'auto',
                    height: '28px'
                  }}
                >
                  Reject
                </Button>
              </>
            )}
            {company.status === "approved" && company.teamMembers.length > 0 && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<Visibility fontSize="small" />}
                onClick={() => handleViewMembersOpen(company._id)}
                sx={{
                  borderColor: theme.palette.secondary.main,
                  color: theme.palette.secondary.main,
                  fontSize: '0.7rem',
                  px: 1,
                  minWidth: 'auto',
                  height: '28px'
                }}
              >
                View
              </Button>
            )}
          </Stack>
        );
      },
    },
  ];

  // 🔹 Render Admin View (Single Company) - Simplified UI
  const renderAdminView = () => {
    if (!adminCompany) {
      return (
        <Box sx={{ 
          textAlign: "center", 
          py: 6, 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center',
          alignItems: 'center',
          px: 2
        }}>
          <Business
            sx={{
              fontSize: { xs: 48, sm: 60 },
              color: "text.secondary",
              mb: 2,
              opacity: 0.4,
            }}
          />
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ fontSize: { xs: "1rem", sm: "1.2rem" }, mb: 1, fontWeight: 500 }}
          >
            No Company Found
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ opacity: 0.6, mb: 3, textAlign: 'center', fontSize: '0.9rem' }}
          >
            Add your first company to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenAddCompanyDialog(true)}
            sx={{
              background: "primary.main",
              "&:hover": { background: alpha(primaryColor, 0.9) },
              px: 3,
              py: 1,
              borderRadius: 2,
              fontWeight: 500,
              fontSize: '0.875rem'
            }}
          >
            Add Company
          </Button>
        </Box>
      );
    }

    return (
      <Box sx={{ p: { xs: 1, sm: 2 }, height: '100%', overflow: 'auto' }}>
        {/* Simplified Company Card */}
        <Card 
          sx={{ 
            mb: 2,
            border: `1px solid ${alpha(primaryColor, 0.1)}`,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            bgcolor: 'background.paper'
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
            {/* Company Header */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' }, 
              justifyContent: 'space-between', 
              alignItems: { xs: 'flex-start', sm: 'center' }, 
              gap: 1,
              mb: 2 
            }}>
              <Box>
                <Typography 
                  variant="h6" 
                  fontWeight="600" 
                  color="primary.main" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem' } }}
                >
                  {adminCompany.name}
                </Typography>
                <Chip
                  label={adminCompany.status.charAt(0).toUpperCase() + adminCompany.status.slice(1)}
                  size="small"
                  sx={{
                    backgroundColor: getStatusColor(adminCompany.status).bg,
                    color: getStatusColor(adminCompany.status).color,
                    fontWeight: '500',
                    fontSize: '0.75rem',
                    height: '24px'
                  }}
                />
              </Box>
              
              <Button
                variant="outlined"
                startIcon={<Edit fontSize="small" />}
                onClick={() => handleEditOpen(adminCompany)}
                sx={{
                  borderColor: theme.palette.warning.main,
                  color: theme.palette.warning.main,
                  borderRadius: 1.5,
                  fontSize: '0.8rem',
                  height: '32px',
                  width: { xs: '100%', sm: 'auto' }
                }}
              >
                Edit Company
              </Button>
            </Box>

            {/* Quick Stats */}
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              mb: 3,
              flexWrap: 'wrap'
            }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" fontWeight="600" color="primary.main">
                  {adminCompany.teamMembers.length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Team Members
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" fontWeight="600" color="primary.main">
                  {adminCompany.status === 'approved' ? 'Active' : 'Inactive'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
              </Box>
            </Box>

            {/* Action Buttons */}
            {adminCompany.status === "approved" && (
              <Box sx={{ 
                display: 'flex', 
                gap: 1, 
                flexWrap: 'wrap',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' }
              }}>
                <Button
                  variant="contained"
                  startIcon={<Add fontSize="small" />}
                  onClick={() => handleAddMembersOpen(adminCompany._id)}
                  sx={{
                    background: "primary.main",
                    "&:hover": { background: alpha(primaryColor, 0.9) },
                    px: 2,
                    py: 0.8,
                    borderRadius: 1.5,
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    height: '32px',
                    width: { xs: '100%', sm: 'auto' }
                  }}
                >
                  Add Members
                </Button>
                
                {adminCompany.teamMembers.length > 0 && (
                  <Button
                    variant="outlined"
                    startIcon={<Visibility fontSize="small" />}
                    onClick={() => handleViewMembersOpen(adminCompany._id)}
                    sx={{
                      borderColor: "primary.main",
                      color: "primary.main",
                      px: 2,
                      py: 0.8,
                      borderRadius: 1.5,
                      fontWeight: 500,
                      fontSize: '0.8rem',
                      height: '32px',
                      width: { xs: '100%', sm: 'auto' }
                    }}
                  >
                    View Members ({adminCompany.teamMembers.length})
                  </Button>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  };

  // 🔹 Render SuperAdmin View (Table with all companies)
  const renderSuperAdminView = () => {
    return (
      <>
        {/* Companies Table */}
        <Box 
          sx={{ 
            border: `1px solid ${alpha(primaryColor, 0.1)}`,
            borderRadius: 2,
            backgroundColor: 'background.paper',
            height: { xs: 'calc(100vh - 160px)', sm: 'calc(100vh - 200px)' },
            minHeight: '400px',
            overflow: 'hidden',
            mx: { xs: 1, sm: 2 },
            mb: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
          }}
        >
          <Card sx={{ height: '100%', boxShadow: 'none', borderRadius: 2 }}>
            <CardContent sx={{ p: 0, height: '100%' }}>
              {filteredCompanies.length === 0 ? (
                <Box sx={{ 
                  textAlign: "center", 
                  py: 6, 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <Business
                    sx={{
                      fontSize: { xs: 48, sm: 60 },
                      color: "text.secondary",
                      mb: 2,
                      opacity: 0.4,
                    }}
                  />
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "1rem", sm: "1.2rem" }, mb: 1, fontWeight: 500 }}
                  >
                    No companies found
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ opacity: 0.6, fontSize: '0.9rem' }}
                  >
                    {isFilterActive 
                      ? "No companies match your filter criteria" 
                      : "No companies available"
                    }
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ height: '100%', width: '100%' }}>
                  <DataGrid
                    rows={rows}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    sx={{
                      border: 'none',
                      '& .MuiDataGrid-cell': {
                        borderBottom: `1px solid ${alpha(primaryColor, 0.08)}`,
                        py: 0.5,
                        fontSize: '0.8rem'
                      },
                      '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: alpha(primaryColor, 0.02),
                        borderBottom: `1px solid ${alpha(primaryColor, 0.15)}`,
                      },
                      '& .MuiDataGrid-columnHeader': {
                        fontWeight: '600',
                        color: "primary.main",
                        fontSize: '0.8rem',
                      },
                      '& .MuiDataGrid-row:hover': {
                        backgroundColor: alpha(accentColor, 0.02),
                      },
                      '& .MuiDataGrid-footerContainer': {
                        borderTop: `1px solid ${alpha(primaryColor, 0.08)}`,
                        fontSize: '0.8rem'
                      },
                      '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                        outline: 'none',
                      },
                      '& .MuiDataGrid-virtualScroller': {
                        minHeight: '150px'
                      }
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </>
    );
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: 'hidden',
        ml: { xs: 0, sm: -2 },
        mt: { xs: 0, sm: -3 },
        bgcolor: 'background.default'
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          pb: 0.5,
          backgroundColor: "transparent",
        }}
      >
        {/* ROW WRAPPER */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: role === "superAdmin" ? 2 : 0.5,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {/* LEFT SIDE - Only superAdmin sees chips */}
          {role === "superAdmin" && isFilterActive ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Chip
                label={`Status: ${statusFilter}`}
                size="small"
                onDelete={handleResetFilters}
                deleteIcon={<Close fontSize="small" />}
                sx={{
                  backgroundColor: getStatusColor(statusFilter).bg,
                  color: getStatusColor(statusFilter).color,
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  height: '26px'
                }}
              />

              <Button
                size="small"
                onClick={handleResetFilters}
                sx={{
                  color: "primary.main",
                  fontWeight: 500,
                  fontSize: "0.75rem",
                  textTransform: "none",
                  minWidth: 'auto'
                }}
              >
                Clear
              </Button>
            </Box>
          ) : (
            role === "superAdmin" && <Box />
          )}

          {/* RIGHT SIDE BUTTONS */}
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            
            {/* ADMIN: Only show Add Company button if no company exists */}
            {role === "admin" && !adminHasCompany && (
              <Button
                variant="contained"
                startIcon={<Add fontSize="small" />}
                onClick={() => setOpenAddCompanyDialog(true)}
                sx={{
                  background: "primary.main",
                  "&:hover": {
                    background: alpha(primaryColor, 0.9),
                  },
                  fontSize: "0.75rem",
                  px: 2,
                  py: 0.8,
                  borderRadius: 1.5,
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  minWidth: { xs: 'auto', sm: '120px' },
                  height: '32px'
                }}
              >
                Add Company
              </Button>
            )}

            {/* SUPERADMIN → Filter Button Only */}
            {role === "superAdmin" && (
              <Button
                variant="outlined"
                startIcon={<FilterList fontSize="small" />}
                onClick={handleFilterClick}
                sx={{
                  borderColor: "primary.main",
                  color: "primary.main",
                  fontSize: "0.75rem",
                  px: 1.5,
                  py: 0.8,
                  borderRadius: 1.5,
                  fontWeight: 500,
                  minWidth: { xs: 'auto', sm: '80px' },
                  height: '32px'
                }}
              >
                Filter
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Filter Popover for superAdmin */}
      {role === "superAdmin" && (
        <Popover
          open={filterOpen}
          anchorEl={filterAnchorEl}
          onClose={handleFilterClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              p: 1.5,
              width: { xs: 160, sm: 180 },
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: `1px solid ${alpha(primaryColor, 0.15)}`,
              borderRadius: 1.5,
              mt: 0.5,
              bgcolor: 'background.paper'
            }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '0.8rem' }}>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ fontSize: '0.8rem' }}
              >
                <MenuItem value="all" sx={{ fontSize: '0.8rem' }}>All Status</MenuItem>
                <MenuItem value="approved" sx={{ fontSize: '0.8rem' }}>Approved</MenuItem>
                <MenuItem value="pending" sx={{ fontSize: '0.8rem' }}>Pending</MenuItem>
                <MenuItem value="rejected" sx={{ fontSize: '0.8rem' }}>Rejected</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={handleResetFilters}
                variant="outlined"
                fullWidth
                size="small"
                sx={{ fontSize: '0.75rem', height: '28px' }}
              >
                Reset
              </Button>
              <Button
                onClick={applyFilters}
                variant="contained"
                fullWidth
                size="small"
                sx={{
                  bgcolor: "primary.main",
                  fontSize: '0.75rem',
                  height: '28px',
                  '&:hover': {
                    bgcolor: alpha(primaryColor, 0.9),
                  }
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>
      )}

      {/* Main Content */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'hidden',
          mt: { xs: 0, sm: -2 }
        }}
      >
        {role === "admin" ? renderAdminView() : renderSuperAdminView()}
      </Box>

      {/* Dialogs (common for both roles) */}
      {/* Add Company Dialog */}
      <Dialog
        open={openAddCompanyDialog}
        onClose={() => setOpenAddCompanyDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${alpha(primaryColor, 0.1)}`,
            m: { xs: 1, sm: 2 },
            bgcolor: 'background.paper'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontWeight: '600',
          fontSize: '1rem',
          py: 2,
          px: 2,
          borderBottom: `1px solid ${alpha(primaryColor, 0.1)}`
        }}>
          <Business fontSize="small" />
          Add New Company
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 2 }}>
          <form onSubmit={handleAddCompany}>
            <Stack spacing={2}>
              <TextField
                label="Company Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                fullWidth
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    fontSize: '0.875rem'
                  },
                  mt: 2 // Added space above the text field
                }}
              />
              {message && (
                <Typography 
                  color={message.includes('✅') ? 'success.main' : 'error.main'}
                  sx={{ fontWeight: 500, fontSize: '0.8rem' }}
                >
                  {message}
                </Typography>
              )}
            </Stack>
          </form>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: `1px solid ${alpha(primaryColor, 0.1)}`,
          py: 1.5,
          px: 2,
          gap: 1
        }}>
          <Button 
            onClick={() => setOpenAddCompanyDialog(false)}
            sx={{ 
              color: 'error.main', 
              fontSize: '0.8rem',
              border: '1px solid',
              borderColor: 'error.main',
              px: 2
            }}
            size="small"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddCompany}
            disabled={loading}
            startIcon={loading && <CircularProgress size={16} />}
            size="small"
            sx={{
              bgcolor: "success.main",
              borderRadius: 1.5,
              fontSize: '0.8rem',
              px: 2,
              '&:hover': {
                bgcolor: alpha(theme.palette.success.main, 0.9),
              }
            }}
          >
            {loading ? "Adding..." : "Add Company"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Members Dialog */}
      <Dialog
        open={openAddMembersPop}
        onClose={handleAddMembersClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${alpha(primaryColor, 0.1)}`,
            m: { xs: 1, sm: 2 },
            bgcolor: 'background.paper'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${alpha(primaryColor, 0.1)}`,
          fontWeight: '600',
          fontSize: '1rem',
          py: 1.5,
          px: 2
        }}>
          Add Team Members
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 2 }}>
          {selectedCompany && (
            <AddTeamMembers
              companyId={selectedCompany}
              onClose={handleAddMembersClose} 
              userData={userData} 
              open={openAddMembersPop} 
            />
          )}
        </DialogContent>
      </Dialog>

      {/* View Members Dialog */}
      <Dialog
        open={openViewMembers}
        onClose={handleViewMembersClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${alpha(primaryColor, 0.1)}`,
            maxHeight: '70vh',
            m: { xs: 1, sm: 2 },
            bgcolor: 'background.paper'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${alpha(primaryColor, 0.1)}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontWeight: '600',
          fontSize: '1rem',
          py: 1.5,
          px: 2
        }}>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            Team Members
          </Typography>
          <IconButton 
            onClick={handleViewMembersClose}
            size="small"
            sx={{ color: "primary.main" }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 2 }}>
          {selectedCompany && <ViewTeamMembers companyId={selectedCompany} />}
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: `1px solid ${alpha(primaryColor, 0.1)}`,
          py: 1.5,
          px: 2
        }}>
          <Button 
            onClick={handleViewMembersClose}
            variant="contained"
            size="small"
            sx={{
              bgcolor: "primary.main",
              borderRadius: 1.5,
              fontSize: '0.8rem',
              px: 2,
              '&:hover': {
                bgcolor: alpha(primaryColor, 0.9),
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Company Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={handleEditClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${alpha(primaryColor, 0.1)}`,
            m: { xs: 1, sm: 2 },
            bgcolor: 'background.paper'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontWeight: '600',
          fontSize: '1rem',
          py: 2,
          px: 2,
          borderBottom: `1px solid ${alpha(primaryColor, 0.1)}`
        }}>
          <Edit fontSize="small" />
          Edit Company
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 1.5, py: 2, px: 2 }}
        >
          <TextField
            label="Company Name"
            name="name"
            value={editData.name}
            onChange={handleEditChange}
            fullWidth
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
                fontSize: '0.875rem'
              },
              mt: 2 // Added space above the text field
            }}
          />
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: `1px solid ${alpha(primaryColor, 0.1)}`,
          py: 1.5,
          px: 2,
          gap: 1
        }}>
          <Button 
            onClick={handleEditClose} 
            size="small"
            sx={{ 
              fontSize: '0.8rem',
              color: 'error.main',
              border: '1px solid',
              borderColor: 'error.main',
              px: 2
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleUpdateCompany}
            size="small"
            sx={{
              bgcolor: "success.main",
              borderRadius: 1.5,
              fontSize: '0.8rem',
              px: 2,
              '&:hover': {
                bgcolor: alpha(theme.palette.success.main, 0.9),
              }
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Companies;